import { useState } from "react";
import { Alert, MenuItem, TextField, Typography } from "@mui/material";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import { Button, useNotify, useRecordContext, useRefresh } from "react-admin";
import { dormitoryRequests } from "api/dormitoryApi";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import dataProvider from "server/dataProvider";
import { useNotifyError } from "utils/useNotifyError";
import { DormitoryManualPaymentMethodChoices } from "utils/choices";
import { Currency } from "utils/constants";

const currencyFormatter = new Intl.NumberFormat("bg-BG", {
  style: "currency",
  currency: Currency.EUR,
  minimumFractionDigits: 2,
});

const RecordPaymentDialog = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const notifyError = useNotifyError();
  const refresh = useRefresh();
  const [method, setMethod] = useState("");
  const [reference, setReference] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // A failed request may still have recorded the money on the backend, so the record is
  // reloaded and further submissions are blocked until the staff member reopens the dialog.
  const [stateReloaded, setStateReloaded] = useState(false);

  const trimmedReference = reference.trim();
  const referenceInvalid = trimmedReference.length > 500;
  const confirmEnabled = !!method && !!trimmedReference && !referenceInvalid && !submitting && !stateReloaded;

  const handleConfirm = async () => {
    if (!confirmEnabled) {
      return false;
    }

    setSubmitting(true);

    try {
      await dataProvider.customRequest(
        dormitoryRequests.recordAccommodationPayment(record.accommodationId, record.id),
        { method: Number(method), amount: record.amount, reference: trimmedReference },
        "POST",
      );
      notify("Плащането е записано", { type: "success" });
      refresh();
    } catch (error) {
      setStateReloaded(true);
      refresh();
      notifyError(error);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmModal
      onConfirm={handleConfirm}
      titleContent="Записване на получено плащане"
      confirmEnabled={confirmEnabled}
      resetProps={() => {
        setMethod("");
        setReference("");
        setSubmitting(false);
        setStateReloaded(false);
      }}
      triggerButton={
        <Button
          startIcon={<PointOfSaleIcon />}
          label="Запиши плащане"
        />
      }
    >
      {stateReloaded && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
        >
          Състоянието на сметката беше презаредено. Затворете прозореца и проверете сметката, преди да записвате плащане
          отново.
        </Alert>
      )}
      <Typography sx={{ mb: 2 }}>Сума за записване: {currencyFormatter.format(record?.amount ?? 0)}</Typography>
      <TextField
        select
        label="Начин на плащане"
        value={method}
        onChange={e => setMethod(e.target.value)}
        disabled={submitting || stateReloaded}
        fullWidth
        sx={{ mb: 2 }}
      >
        {DormitoryManualPaymentMethodChoices.map(choice => (
          <MenuItem
            key={choice.id}
            value={choice.id}
          >
            {choice.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Номер на документ / банкова референция"
        value={reference}
        onChange={e => setReference(e.target.value)}
        disabled={submitting || stateReloaded}
        error={referenceInvalid}
        helperText={referenceInvalid ? "Референцията трябва да е до 500 символа." : `${trimmedReference.length}/500`}
        multiline
        rows={2}
        fullWidth
      />
    </ConfirmModal>
  );
};

export default RecordPaymentDialog;
